import { referenceDisplay } from '@seen/core/display'
import type { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { hasReference } from '@seen/core/utils'

import { cn, TypographyDetail } from '@seen/ui'
import { IconPencil } from '@tabler/icons-react'
import { type JSX, useState } from 'react'
import { CommentEditor } from '#src/components/activity/CommentEditor.tsx'
import { CommentRelatedTasks } from '#src/components/activity/CommentRelatedTasks.tsx'
import { DropdownMenuNewTaskFromCommentItem } from '#src/components/activity/DropdownMenuNewTaskFromCommentItem.tsx'
import { EditHistorySheet } from '#src/components/activity/EditHistorySheet.tsx'
import { useComposeTaskFromComment } from '#src/components/activity/useComposeTaskFromComment.ts'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Card } from '#src/components/core/Card.tsx'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { TaskableRichText } from '#src/components/TaskableRichText.tsx'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFhirReadHistory } from '#src/hooks/useFhirReadHistory.ts'
import { useTimelineItemHashMatch } from '#src/hooks/useHashMatch.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useInMemoryStore } from '#src/hooks/useInMemoryStore.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useScrollHighlight } from '#src/hooks/useScrollHighlight.ts'

export function CommentReply({
  comment,
  dateComponent = (date) => <HoverDate date={date} format="datetime" />,
}: {
  comment: CommentCommunication
  dateComponent?: (date: string | Date) => JSX.Element
}): JSX.Element {
  const currentPractitioner = useCurrentPractitioner()
  const isReadOnly = useIsReadOnly()
  const mutateComment = usePatchMutation<CommentCommunication>().mutate
  const [editing, setEditing] = useState(false)
  const { data: history } = useFhirReadHistory('Communication', comment.id)
  const isEdited = (history?.entry?.length ?? 0) > 1

  const elementId = `comment-${comment.id}`
  const highlightCommentId = useInMemoryStore((s) => s.highlightCommentId)
  const hashMatch = useTimelineItemHashMatch(comment.id)
  const { ref: highlightRef, highlightClassName } = useScrollHighlight(
    highlightCommentId === elementId || hashMatch
  )

  const [hovering, setHovering] = useState(false)
  const composeTaskFromComment = useComposeTaskFromComment(comment)

  const isGloballyShowingTaskableTextButton = useInMemoryStore(
    (state) => state.isShowingTaskableTextButton
  )
  useHotkeys(Hotkeys.NewTaskFromText, () => composeTaskFromComment(), {
    enabled: hovering && !isGloballyShowingTaskableTextButton,
  })

  const isCommentAuthor = hasReference(
    comment.sender,
    currentPractitioner.reference
  )

  return (
    <div
      id={elementId}
      ref={highlightRef}
      className={cn('group flex flex-col gap-1.5 p-4', highlightClassName)}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div className="flex justify-between gap-2">
        <div className="flex items-center gap-2">
          <Avatar profile={comment.sender} size="icon" />
          <TypographyDetail className="flex items-center gap-2 truncate">
            {comment.practitionerSender ? (
              <HoverPractitioner
                practitioner={comment.practitionerSender}
                className="truncate"
              />
            ) : (
              <span className="truncate font-medium">
                {referenceDisplay(comment.sender)}
              </span>
            )}
          </TypographyDetail>
          <TypographyDetail className="font-normal text-nowrap text-foreground-secondary">
            {dateComponent(comment.sent)}
            {isEdited && (
              <span className="text-foreground-secondary">
                {' '}
                <EditHistorySheet comment={comment}>(edited)</EditHistorySheet>
              </span>
            )}
          </TypographyDetail>
        </div>
        {!editing && !isReadOnly && (
          <div className="-mr-2.5 flex">
            <MoreDropdownMenu className="invisible group-hover:visible data-[state=open]:visible">
              {isCommentAuthor && (
                <DropdownMenuItem
                  className="flex items-center gap-2"
                  onSelect={() => setEditing(true)}
                >
                  <IconPencil className="-mt-px size-4 text-foreground-secondary" />
                  Edit
                </DropdownMenuItem>
              )}
              <DropdownMenuNewTaskFromCommentItem comment={comment} />
              <DropdownMenuDeleteItem
                deleteConfirmationProps={{
                  title: `Delete reply?`,
                  description: (
                    <div className="flex flex-col gap-4 select-none">
                      <Card>
                        <RichText value={comment.message} className="text-sm" />
                      </Card>
                      <span>
                        Deleted replies can still be recovered by contacting our
                        team.
                      </span>
                    </div>
                  ),
                  onConfirm: () => {
                    mutateComment({
                      instance: comment,
                      mutateFn: (c) => {
                        c.markDeleted()
                      },
                    })
                  },
                }}
              />
              <DropdownMenuMedplumLinkItem to={comment} />
            </MoreDropdownMenu>
          </div>
        )}
      </div>
      <div className="ml-7">
        {!editing && (
          <div>
            <TaskableRichText
              value={comment.message}
              className="text-sm"
              onSelectCreateTask={composeTaskFromComment}
            />
            <CommentRelatedTasks comment={comment} />
          </div>
        )}
        {editing && (
          <CommentEditor comment={comment} onClose={() => setEditing(false)} />
        )}
      </div>
    </div>
  )
}
